import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../../components/Button';
import { Field, Input, Select } from '../../components/Field';
import { Card, Page } from '../../components/Page';
import { useDb } from '../../db/hooks';
import { saveSetting, useSettings, type AppSettings } from '../../data/settings';
import { useCanWrite } from '../../account/useCanWrite';
import { currencyOptions } from '../../lib/currency';
import { inputValue, optionalNumber } from '../../lib/forms';

const schema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  fluid_limit_ml: optionalNumber(100, 5000, 'Enter an amount between 100 and 5000 mL.'),
  week_starts_on: z.enum(['mon', 'sun'])
});

type FormInput = z.input<typeof schema>;
type FormOutput = z.output<typeof schema>;

const toForm = (settings: AppSettings): FormInput => ({
  currency: settings.currency,
  fluid_limit_ml: inputValue(settings.fluid_limit_ml),
  week_starts_on: settings.week_starts_on
});

export function SettingsPage() {
  const db = useDb();
  const canWrite = useCanWrite();
  const { settings, isLoading } = useSettings();
  const currencies = useMemo(() => currencyOptions(), []);
  const [saved, setSaved] = useState(false);
  const form = useForm<FormInput, unknown, FormOutput>({ resolver: zodResolver(schema), defaultValues: toForm(settings) });
  const { errors, isSubmitting, isDirty } = form.formState;

  useEffect(() => {
    if (!isLoading && !form.formState.isDirty) form.reset(toForm(settings));
    // settings is rebuilt each render; its values are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, settings.currency, settings.fluid_limit_ml, settings.week_starts_on, form]);

  async function submit(values: FormOutput) {
    await saveSetting(db, 'currency', values.currency);
    await saveSetting(db, 'fluid_limit_ml', values.fluid_limit_ml);
    await saveSetting(db, 'week_starts_on', values.week_starts_on);
    form.reset(form.getValues());
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <Page title="Settings" back="/more">
      <form noValidate onSubmit={form.handleSubmit(submit)}>
        <fieldset disabled={!canWrite} className="space-y-4">
          <Card title="Daily tracking">
            <Field
              label="Daily fluid limit (mL)"
              help="Everything you drink in a day, as advised by your care team. Leave empty if you don’t have one."
              error={errors.fluid_limit_ml?.message}
            >
              <Input inputMode="numeric" {...form.register('fluid_limit_ml')} />
            </Field>
          </Card>

          <Card title="Display">
            <div className="space-y-4">
              <Field label="Currency for expenses">
                <Select {...form.register('currency')}>
                  {currencies.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Week starts on">
                <Select {...form.register('week_starts_on')}>
                  <option value="mon">Monday</option>
                  <option value="sun">Sunday</option>
                </Select>
              </Field>
              <p className="text-xs text-clinical-muted">Units are kilograms, millilitres and mg/dL. Lab results keep the units from your report.</p>
            </div>
          </Card>

          {canWrite ? (
            <Button type="submit" className="w-full" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? 'Saving…' : saved ? 'Saved' : 'Save settings'}
            </Button>
          ) : null}
        </fieldset>
      </form>
    </Page>
  );
}
